import React, { useRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { LocalTransaction } from '../utils/TransactionFormatters';
import TransactionCard from '../TransactionCard';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/button';

interface OptimizedMobileTransactionViewProps {
  transactions: LocalTransaction[];
  selectedTransactions: Set<string>;
  isLoading: boolean;
  hasMoreData: boolean;
  pageSize: number;
  loadingMoreText: string;
  onLoadMore: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

// Batch size for rendering optimization
const MOBILE_RENDER_BATCH_SIZE = 20;
const MOBILE_RENDER_DELAY = 16; // ~60fps

// Memoized transaction card component
const OptimizedTransactionCard = React.memo<{
  transaction: LocalTransaction;
  isSelected: boolean;
}>(({ transaction, isSelected }) => (
  <div className="relative">
    <TransactionCard transaction={transaction as any} />
    {isSelected && (
      <div className="absolute top-2 right-2 z-10">
        <Checkbox
          checked={true}
          className="h-5 w-5 bg-primary border-primary text-white"
        />
      </div>
    )}
  </div>
), (prevProps, nextProps) => {
  // Only re-render if transaction or selection state changed
  return (
    prevProps.transaction.id === nextProps.transaction.id &&
    prevProps.transaction.updatedAt === nextProps.transaction.updatedAt &&
    prevProps.isSelected === nextProps.isSelected
  );
});

OptimizedTransactionCard.displayName = 'OptimizedTransactionCard';

export const OptimizedMobileTransactionView: React.FC<OptimizedMobileTransactionViewProps> = ({
  transactions,
  selectedTransactions,
  isLoading,
  hasMoreData,
  pageSize,
  loadingMoreText,
  onLoadMore,
  t
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const lastLoadTimeRef = useRef<number>(0);
  const [renderCount, setRenderCount] = useState(MOBILE_RENDER_BATCH_SIZE);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const loadMoreTrigger = loadMoreTriggerRef.current;
    const scrollContainer = scrollContainerRef.current;
    
    if (!loadMoreTrigger || !hasMoreData || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        if (entry.isIntersecting) {
          const now = Date.now();
          const timeSinceLastLoad = now - lastLoadTimeRef.current;
          
          // More conservative approach: increase threshold and add transaction count check
          const shouldLoad = timeSinceLastLoad > 3000 && // Increased to 3 seconds
                           hasMoreData && 
                           !isLoading &&
                           transactions.length < 2000; // Don't auto-load beyond 2000 transactions
          
          if (shouldLoad) {
            lastLoadTimeRef.current = now;
            onLoadMore();
          }
        }
      },
      {
        root: scrollContainer,
        rootMargin: '100px 0px', // Reduced from 200px to be less aggressive
        threshold: 0.1
      }
    );

    observer.observe(loadMoreTrigger);

    return () => {
      observer.unobserve(loadMoreTrigger);
      observer.disconnect();
    };
  }, [hasMoreData, isLoading, onLoadMore, transactions.length]);

  // Progressive rendering effect
  useEffect(() => {
    if (renderCount < transactions.length) {
      const timeoutId = setTimeout(() => {
        setRenderCount(prev => Math.min(prev + MOBILE_RENDER_BATCH_SIZE, transactions.length));
      }, MOBILE_RENDER_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [renderCount, transactions.length]);

  // Adjust render count based on transaction changes
  useEffect(() => {
    if (transactions.length === 0) {
      // No transactions - reset to batch size
      setRenderCount(MOBILE_RENDER_BATCH_SIZE);
    } else if (transactions.length < renderCount * 0.5) {
      // Significant decrease (filter change, data reset) - reset to batch size
      setRenderCount(MOBILE_RENDER_BATCH_SIZE);
    } else if (transactions.length > renderCount) {
      // More transactions available than currently rendered - expand to show all
      setRenderCount(transactions.length);
    }
  }, [transactions.length, renderCount]);

  // Memoize the transactions to render
  const transactionsToRender = useMemo(() => {
    return transactions.slice(0, renderCount);
  }, [transactions, renderCount]);

  if (isLoading && transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="mt-2 text-sm text-text-secondary dark:text-text-dark-secondary">
          {t('transactions.loading')}
        </span>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <div className="rounded-full bg-info/10 p-3 mb-3">
          <Receipt className="h-8 w-8 text-info" />
        </div>
        <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary">
          {t('transactions.noTransactions')}
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1 max-w-md text-center px-4">
          {t('transactions.noTransactionsDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-2" ref={scrollContainerRef}>
      <div className="grid grid-cols-1 gap-3 pb-16">
        {transactionsToRender.map(transaction => (
          <OptimizedTransactionCard
            key={transaction.id}
            transaction={transaction}
            isSelected={selectedTransactions.has(transaction.id)}
          />
        ))}
        
                 {/* Progressive rendering indicator */}
         {renderCount < transactions.length && (
           <div className="flex items-center justify-center p-4">
             <div className="flex items-center">
               <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
               <span className="text-sm text-gray-500 dark:text-gray-400">
                 Loading more... ({renderCount} of {transactions.length})
               </span>
             </div>
           </div>
         )}
        
        {/* Infinite scroll trigger - always show when we have more data, regardless of render count */}
        {hasMoreData && (
          <div 
            ref={loadMoreTriggerRef}
            className="h-1 w-full"
            style={{ visibility: 'hidden' }}
          />
        )}
        
        {/* Loading indicator and manual load more button */}
        {(isLoading || hasMoreData) && (
          <div className="flex items-center justify-center p-4">
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2"></div>
                <span>{loadingMoreText}</span>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={onLoadMore}>
                {t('transactions.loadMore', 'Load more')} ({pageSize})
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 